/**
 * 完善企业信息引导弹窗 —— 注册最小化后的登录后补填。
 * 两种模式：
 * 1) 自动模式（默认，controlledOpen 不传）：已登录且 user.companyName 为空时自动弹一次，
 *    跳过后写 localStorage 标记（按 buyerId），后续登录不再打扰。
 * 2) 受控模式（传 controlledOpen）：由「我的」页入口手动唤起，关闭经 onDismiss 外呼。
 */
import { Box, Dialog, DialogContent, DialogTitle, TextField, Button, Typography, Autocomplete } from '@mui/material';
import { useEffect, useState } from 'react';
import { useBuyerAuth } from '@/store/BuyerAuthContext';
import { usePrefs } from '@/store/BuyerPrefsContext';
import { listCountries } from '@/api/dictApi';
import { localizeText } from '@/i18n/catalog';

/** 国家/地区快捷选项兜底常量（数据字典 country 加载失败时回退；正式候选以 sys_dict_data 为准，未列出仍可 freeSolo 手输） */
const COUNTRY_OPTIONS = [
  '阿联酋', '沙特阿拉伯', '卡塔尔', '科威特', '阿曼', '巴林', '伊拉克', '土耳其', '埃及', '摩洛哥',
  '马来西亚', '印度尼西亚', '泰国', '越南', '菲律宾', '新加坡', '日本', '韩国', '印度', '巴基斯坦',
  '孟加拉国', '斯里兰卡', '荷兰', '德国', '英国', '法国', '西班牙', '意大利', '俄罗斯', '美国',
  '加拿大', '澳大利亚', '新西兰', '巴西', '南非', '尼日利亚', '肯尼亚', '坦桑尼亚', '安哥拉', '加纳',
];

const DISMISS_PREFIX = 'bp_buyer_profile_dismissed_';

interface ProfileCompleteDialogProps {
  /** 受控模式开关；不传则走自动模式 */
  controlledOpen?: boolean;
  /** 受控模式关闭回调（跳过/保存成功/点遮罩） */
  onDismiss?: () => void;
  /** 强制模式：隐藏「跳过」、禁遮罩关闭，仅保存成功后关闭（发布询价前置闸门用） */
  force?: boolean;
  /** 保存成功回调（强制模式下供调用方继续此前被拦截的操作，如自动继续提交询盘） */
  onSaved?: () => void;
}

export default function ProfileCompleteDialog({ controlledOpen, onDismiss, force, onSaved }: ProfileCompleteDialogProps) {
  const { isAuthenticated, user, updateProfile } = useBuyerAuth();
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /** 国家候选：优先数据字典 country（sys_dict_data，运营可维护）；加载失败/为空回退内置兜底常量 */
  const [countryOptions, setCountryOptions] = useState<string[]>(COUNTRY_OPTIONS);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    listCountries().then((opts) => { if (alive && opts.length > 0) setCountryOptions(opts); });
    return () => { alive = false; };
  }, [open]);

  const dismissed = (() => {
    try {
      return localStorage.getItem(DISMISS_PREFIX + (user?.buyerId ?? '')) === '1';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    // 受控模式：完全由外部开关驱动
    if (controlledOpen !== undefined) {
      setOpen(controlledOpen);
      return;
    }
    // 自动模式：登录且公司名缺失且未跳过 → 弹出；仅依赖登录态变化，避免输入中重弹
    if (isAuthenticated && user && !user.companyName && !dismissed) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.buyerId, controlledOpen]);

  // 打开时用已保存的概要回显（再次打开/改信息场景不丢已填内容）
  useEffect(() => {
    if (!open) return;
    setCompany(user?.companyName ?? '');
    setContact(user?.contactName ?? '');
    setPhone(user?.phone ?? '');
    setCountry(user?.country ? localizeText(user.country) : null);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    onDismiss?.();
  };

  const handleSkip = () => {
    try {
      localStorage.setItem(DISMISS_PREFIX + (user?.buyerId ?? ''), '1');
    } catch {
      /* ignore */
    }
    close();
  };

  const handleSave = async () => {
    setError('');
    if (!company.trim()) {
      setError(t('profile.companyRequired'));
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        buyerId: user!.buyerId,
        companyName: company.trim(),
        contactName: contact.trim() || undefined,
        country: toRecordCountry((country ?? '').trim()) || undefined,
        phone: phone.trim() || undefined,
      });
      // 先通知调用方「已保存」（供强制模式继续被拦截的操作），再关闭
      onSaved?.();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** 选项按当前语言展示，保存时换回中文原值（后台与数据字典以中文为准） */
  const toRecordCountry = (value: string) => countryOptions.find((option) => option === value || localizeText(option) === value) ?? value;

  if (!open) return null;

  return (
    <Dialog open={open} onClose={force ? undefined : handleSkip} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 700, pb: 0.5 }}>{t('profile.title')}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: '12px', color: '#757575', mb: 2 }}>
          {force ? t('profile.descForce') : t('profile.desc')}
        </Typography>
        {error && (
          <Typography sx={{ fontSize: '12px', color: 'error.main', mb: 1 }}>{error}</Typography>
        )}
        <TextField
          size="small" fullWidth label={t('profile.company')} value={company}
          onChange={(e) => setCompany(e.target.value)} sx={{ mb: 1.5 }}
        />
        <TextField
          size="small" fullWidth label={t('profile.contact')} value={contact}
          onChange={(e) => setContact(e.target.value)} sx={{ mb: 1.5 }}
        />
        <TextField
          size="small" fullWidth label={t('profile.phone')} value={phone}
          onChange={(e) => setPhone(e.target.value)} sx={{ mb: 1.5 }}
        />
        <Autocomplete
          freeSolo size="small"
          options={countryOptions}
          getOptionLabel={(option) => localizeText(option)}
          value={country}
          onInputChange={(_, v) => setCountry(v)}
          renderInput={(params) => (
            <TextField {...params} size="small" label={t('profile.country')} sx={{ mb: 2 }} />
          )}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!force && (
            <Button variant="text" onClick={handleSkip} sx={{ flex: 1, textTransform: 'none', fontSize: '13px', color: '#757575' }}>
              {t('profile.skip')}
            </Button>
          )}
          <Button variant="contained" disabled={saving} onClick={handleSave} sx={{ flex: force ? 1 : 2, textTransform: 'none', fontSize: '13px', fontWeight: 600 }}>
            {saving ? '...' : t('profile.save')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
